import React, { useEffect, useState } from 'react';
import {
  BarChart3, Users, Building, Settings, TrendingUp,
  DollarSign, Activity, AlertCircle, CheckCircle,
  RefreshCw, Calendar, ArrowUpRight, ArrowDownRight
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { userApi } from '@/api/users';
import { bankApi, BankStats } from '@/api/banks';
import { apiClient } from '@/api/client';
import toast from 'react-hot-toast';

const initialStats = {
  totalUsers: 0,
  activeUsers: 0,
  totalBanks: 0,
  activeBanks: 0,
  totalInvestments: 0,
  totalTransactions: 0,
  systemHealth: 'healthy',
  monthlyGrowth: 0,
  avgInterestRate: 0,
};

const AdminDashboard = () => {
  const [stats, setStats] = useState(initialStats);
  const [activities, setActivities] = useState<any[]>([]); // Son aktiviteler
  const [showAllActivities, setShowAllActivities] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Load data on component mount
  useEffect(() => {
    refreshData();
  }, []);

  const refreshData = async () => {
    setIsLoading(true);
    try {
      const [userResponse, bankResponse, bankStats] = await Promise.all([
        userApi.list({ page: 1, page_size: 100, is_active: true, sort_by: 'created_at', sort_order: 'desc' }),
        bankApi.list({ page: 1, page_size: 100, is_active: true }),
        bankApi.getStats(),

      ]);

      // Calculate statistics from response data
      const totalUsers = userResponse.total; // Now only counts active users
      const activeUsers = userResponse.users.length; // All fetched users are active
      const totalBanks = bankResponse.total; // Now only counts active banks
      const activeBanks = bankResponse.banks.length; // All fetched banks are active
      const totalInvestedAmount = 0;

      setStats(prev => ({
        ...prev,
        totalUsers,
        activeUsers,
        totalBanks,
        activeBanks,
        avgInterestRate: (bankStats?.average_rate ?? 0),
        totalInvestments: totalInvestedAmount,
        systemHealth: 'healthy',
      }));

      // Generate real activities from API data
      const recentActivities = generateRecentActivities(userResponse.users, bankResponse.banks);
      setActivities(recentActivities);

      toast.success('Dashboard verileri güncellendi!');
    } catch (error: any) {
      const errorMessage = error.message || 'Veri güncellenirken hata oluştu';
      toast.error(errorMessage);
      console.error('Dashboard data fetch error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Generate recent activities from real data
  const generateRecentActivities = (users: any[], banks: any[]) => {
    const activities: any[] = [];

    // Add user registrations (latest users)
    users.slice(0, 3).forEach((user, index) => {
      const timeAgo = getTimeAgo(user.created_at);
      const roleText = user.role === 'admin' ? 'Yönetici' :
                      user.role === 'staff' ? 'Personel' : 'Müşteri';
      activities.push({
        id: `user_${user.id}`,
        type: 'user_register',
        message: `Yeni ${roleText.toLowerCase()} kaydı: ${user.email}`,
        timestamp: timeAgo,
        created_at: user.created_at
      });
    });

    // Add bank updates (latest banks)
    banks.slice(0, 2).forEach((bank, index) => {
      const timeAgo = getTimeAgo(bank.updated_at || bank.created_at);
      const isNewBank = !bank.updated_at || bank.updated_at === bank.created_at;
      const message = isNewBank ?
        `Yeni banka eklendi: ${bank.name}` :
        `Banka güncellendi: ${bank.name}`;

      activities.push({
        id: `bank_${bank.id}`,
        type: isNewBank ? 'bank_create' : 'bank_update',
        message,
        timestamp: timeAgo,
        created_at: bank.updated_at || bank.created_at
      });
    });

    // Add system activity
    activities.push({
      id: 'system_1',
      type: 'system',
      message: 'Dashboard verileri güncellendi',
      timestamp: 'Az önce',
      created_at: new Date().toISOString()
    });

    // Tarihe göre sırala (yeniden eskiye) ve TAM listeyi döndür
    return activities
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  };

  // Helper function to calculate time ago
  const getTimeAgo = (dateString: string) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));

    if (diffInMinutes < 1) return 'Az önce';
    if (diffInMinutes < 60) return `${diffInMinutes} dakika önce`;

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours} saat önce`;

    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays} gün önce`;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const activityDot = (type: string) => {
    if (type === 'user_register') return 'bg-primary-600';
    if (type === 'investment') return 'bg-success-600';
    if (type === 'bank_update') return 'bg-warning-600';
    if (type === 'bank_create') return 'bg-gold-500';
    if (type === 'transaction') return 'bg-gold-500';
    return 'bg-slate-400';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="relative bg-primary-950 border-l-4 border-gold-500 p-6 sm:p-7 text-white overflow-hidden">
        <div className="relative z-10 flex flex-col sm:flex-row justify-between sm:items-center gap-4">
          <div>
            <span className="text-xs font-semibold uppercase tracking-[0.14em] text-gold-400 mb-1.5 block">Sky View GmbH</span>
            <h1 className="text-2xl sm:text-3xl font-serif font-medium mb-1.5">Admin Dashboard</h1>
            <p className="text-primary-200 text-sm">Yatırım platformu yönetim paneli</p>
          </div>
          <Button
            onClick={refreshData}
            disabled={isLoading}
            variant="outline"
            className="bg-white/10 border-white/20 text-white hover:bg-white/20 shrink-0"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Yenile
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        {/* Total Users */}
        <div className="bg-white p-6 border border-slate-200 border-t-2 border-t-primary-600">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Toplam Kullanıcı</p>
              <p className="text-2xl font-serif font-medium text-slate-900 mt-1.5 tabular-nums">{stats.totalUsers.toLocaleString()}</p>
              <div className="flex items-center mt-2 text-sm text-success-600">
                <ArrowUpRight className="w-4 h-4 mr-1" />
                <span>+{stats.monthlyGrowth}% bu ay</span>
              </div>
            </div>
            <div className="w-11 h-11 bg-primary-50 flex items-center justify-center shrink-0">
              <Users className="w-5 h-5 text-primary-700" />
            </div>
          </div>
        </div>

        {/* Active Banks */}
        <div className="bg-white p-6 border border-slate-200 border-t-2 border-t-gold-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Aktif Bankalar</p>
              <p className="text-2xl font-serif font-medium text-slate-900 mt-1.5 tabular-nums">{stats.activeBanks}/{stats.totalBanks}</p>
              <div className="flex items-center mt-2 text-sm text-gold-700">
                <CheckCircle className="w-4 h-4 mr-1" />
                <span>%{((stats.activeBanks/stats.totalBanks)*100).toFixed(0)} aktif</span>
              </div>
            </div>
            <div className="w-11 h-11 bg-gold-50 flex items-center justify-center shrink-0">
              <Building className="w-5 h-5 text-gold-700" />
            </div>
          </div>
        </div>

        {/* Total Investments */}
        <div className="bg-white p-6 border border-slate-200 border-t-2 border-t-primary-600">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Toplam Yatırım</p>
              <p className="text-2xl font-serif font-medium text-slate-900 mt-1.5 tabular-nums">{formatCurrency(stats.totalInvestments)}</p>
              <div className="flex items-center mt-2 text-sm text-success-600">
                <TrendingUp className="w-4 h-4 mr-1" />
                <span>Ortalama %{stats.avgInterestRate} faiz</span>
              </div>
            </div>
            <div className="w-11 h-11 bg-success-50 flex items-center justify-center shrink-0">
              <DollarSign className="w-5 h-5 text-success-600" />
            </div>
          </div>
        </div>

        {/* System Health */}
        <div className="bg-white p-6 border border-slate-200 border-t-2 border-t-gold-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Sistem Durumu</p>
              <p className="text-2xl font-serif font-medium text-success-600 mt-1.5 capitalize">{stats.systemHealth}</p>
              <div className="flex items-center mt-2 text-sm text-slate-500">
                <Activity className="w-4 h-4 mr-1" />
                <span>Tüm servisler aktif</span>
              </div>
            </div>
            <div className="w-11 h-11 bg-success-50 flex items-center justify-center shrink-0">
              <CheckCircle className="w-5 h-5 text-success-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Activities */}
        <div className="lg:col-span-2 bg-white p-6 border border-slate-200 border-t-2 border-t-primary-600">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-serif font-medium text-slate-900">Son Aktiviteler</h2>
            <Button variant="ghost" size="sm" onClick={() => setShowAllActivities(prev => !prev)}>
              <Calendar className="w-4 h-4 mr-2" />
              {showAllActivities ? 'Daha Az' : 'Tümünü Gör'}
            </Button>
          </div>
          <div className="space-y-1">
            {(showAllActivities ? activities : activities.slice(0, 5)).map((activity) => (
              <div key={activity.id} className="flex items-start gap-3 p-3 border border-transparent hover:bg-slate-50 hover:border-slate-100 transition-colors">
                <div className={`w-1.5 h-1.5 mt-2 shrink-0 ${activityDot(activity.type)}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-slate-900">{activity.message}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{activity.timestamp}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white p-6 border border-slate-200 border-t-2 border-t-gold-500">
          <h2 className="text-lg font-serif font-medium text-slate-900 mb-4">Hızlı İşlemler</h2>
          <div className="space-y-3">
            <Button variant="outline" className="w-full justify-start" onClick={() => window.location.href = '/admin/users'}>
              <Users className="w-4 h-4 mr-3" />
              Kullanıcı Yönetimi
            </Button>
            <Button variant="outline" className="w-full justify-start" onClick={() => window.location.href = '/admin/banks'}>
              <Building className="w-4 h-4 mr-3" />
              Banka Yönetimi
            </Button>
            <Button variant="outline" className="w-full justify-start" onClick={() => window.location.href = '/admin/investments'}>
              <BarChart3 className="w-4 h-4 mr-3" />
              Yatırımlar
            </Button>
            <Button variant="outline" className="w-full justify-start" onClick={() => window.location.href = '/admin/settings'}>
              <Settings className="w-4 h-4 mr-3" />
              Sistem Ayarları
            </Button>
          </div>

          {/* System Status */}
          <div className="mt-6 pt-6 border-t border-slate-200">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-3">Sistem Durumu</h3>
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-600">API Durumu</span>
                <span className="text-success-600 flex items-center">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Aktif
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-600">Database</span>
                <span className="text-success-600 flex items-center">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Bağlı
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-600">Son Yedekleme</span>
                <span className="text-slate-500">2 saat önce</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
